"use client";

import dynamic from "next/dynamic";
import type { ReactNode } from "react";
import BirthToolFormShell from "@/components/tool-shell/BirthToolFormShell";
import { useDeferredToolActivation } from "@/components/tool-shell/useDeferredToolActivation";

// Bundle-size discipline (2026-07-16 trim pass): the interactive island
// (BirthDetailsForm + PlaceAutocomplete + Explainable + result/offer stack)
// lives in PitraDoshClient and loads via next/dynamic, so it leaves this
// route's initial JS and the bundler shares ONE copy with the other deferred
// calculator islands. The header/H1 + a height-reserving form shell render
// immediately (rules 43/61 tool-first viewport; rule 58 reserved swap height).
const PitraDoshToolIsland = dynamic(() => import("./PitraDoshClient"), {
  ssr: false,
  loading: () => <BirthToolFormShell />,
});

export default function DeferredPitraDoshClient({
  defaultLanguage = "en",
  crossLinks,
}: {
  defaultLanguage?: "en" | "hi";
  crossLinks: ReactNode;
}) {
  const { ready, activate } = useDeferredToolActivation();

  return (
    <div
      className="intent-interface-diagnostic-first sacred-bg min-h-[calc(100vh-130px)]"
      data-design-owner="pitra-dosh-calculator-shell"
    >
      <div className="mx-auto max-w-2xl px-4 sm:px-6 py-12 sm:py-16">
        <PitraDoshHeader />

        {ready ? (
          <PitraDoshToolIsland defaultLanguage={defaultLanguage} />
        ) : (
          <BirthToolFormShell
            language={defaultLanguage}
            submitLabel={defaultLanguage === "hi" ? "Pitra Dosh check karein" : "Check Pitra Dosh"}
            onActivate={activate}
          />
        )}

        {crossLinks}
      </div>
    </div>
  );
}

function PitraDoshHeader() {
  return (
    <div className="mb-10 text-center" data-design-owner="pitra-dosh-calculator-header">
      <p className="mb-3 text-eyebrow font-semibold uppercase tracking-eyebrow text-intent-brand">
        Pitra Dosh Pariksha
      </p>
      <h1 className="font-serif text-headline font-bold leading-tight text-text-primary sm:text-display">
        Free Pitra Dosh Calculator
      </h1>
      <p className="mx-auto mt-3 max-w-md text-body leading-relaxed text-text-secondary">
        Enter your birth details and check for Pitra Dosh (ancestral karmic debt) in your chart.
        Instant Sun/node affliction analysis, 9th house check, and Vedic upay — 100% free.
      </p>
      <div className="mt-5 flex flex-wrap justify-center gap-x-5 gap-y-2 text-eyebrow font-medium text-text-muted">
        <span className="flex items-center gap-1.5">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
          Vedic Accuracy
        </span>
        <span className="flex items-center gap-1.5">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v6l4 2" />
          </svg>
          Instant Results
        </span>
        <span className="flex items-center gap-1.5">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
          100% Free
        </span>
      </div>
    </div>
  );
}
